import React, { FC, useState, useEffect, useRef } from 'react';
import { useThreadLoading } from '../../../contexts/ThreadLoadingContext';
import { PhoneCallStartedEvent, PhoneCallEndedEvent } from '@dreamai/types';
import { ReactComponent as AnimatedPhoneIcon } from '../../../icons/animated-phone.svg';
import { ReactComponent as StaticPhoneIcon } from '../../../icons/phone.svg';

import './call-display.scss';

interface CallDisplayProps {
  threadId: string | null;
  onScrollNeeded?: () => void;
  fetchThreadMessages?: (threadId: string) => Promise<void>;
}

const CallDisplay: FC<CallDisplayProps> = ({
  threadId,
  onScrollNeeded,
  fetchThreadMessages,
}) => {
  const [isCallActive, setIsCallActive] = useState(false);
  const [animationState, setAnimationState] = useState<
    'hidden' | 'icon-only' | 'expanding' | 'full' | 'collapsing'
  >('hidden');
  const [callData, setCallData] = useState<{
    phoneNumber?: string;
    agentName?: string;
    startTime?: Date;
  } | null>(null);

  // Use ref to store fetchThreadMessages to avoid closure issues
  const fetchThreadMessagesRef = useRef(fetchThreadMessages);
  fetchThreadMessagesRef.current = fetchThreadMessages;

  const {
    registerPhoneCallStartedCallback,
    unregisterPhoneCallStartedCallback,
    registerPhoneCallEndedCallback,
    unregisterPhoneCallEndedCallback,
  } = useThreadLoading();

  useEffect(() => {
    console.log('CallDisplay: useEffect running, threadId:', threadId);
    if (!threadId) return;

    const callbackId = `call-display-${threadId}`;
    console.log('CallDisplay: Registering callbacks with ID:', callbackId);

    // Register phone call started callback
    registerPhoneCallStartedCallback(
      callbackId,
      (eventThreadId: string, data: PhoneCallStartedEvent) => {
        console.log('Debug: Phone call started', eventThreadId, threadId);
        if (eventThreadId === threadId) {
          console.log('Debug: Phone call started for this thread');
          setIsCallActive(true);
          setCallData({
            phoneNumber: data.phoneNumber,
            agentName: data.agentName,
            startTime: new Date(),
          });

          // Start animation sequence: icon appears first
          setAnimationState('icon-only');
          onScrollNeeded?.();

          // Then expand to show text after a short delay
          setTimeout(() => {
            setAnimationState('expanding');
            onScrollNeeded?.();
          }, 300);

          // Finally show full state
          setTimeout(() => {
            setAnimationState('full');
            onScrollNeeded?.();
          }, 600);
        }
      },
    );

    // Register phone call ended callback
    registerPhoneCallEndedCallback(
      callbackId,
      (eventThreadId: string, data: PhoneCallEndedEvent) => {
        if (eventThreadId === threadId) {
          setIsCallActive(false);

          // Start collapse animation: hide text first
          setAnimationState('collapsing');
          onScrollNeeded?.();

          // Then show icon only
          setTimeout(() => {
            setAnimationState('icon-only');
            onScrollNeeded?.();
          }, 300);

          // Finally hide completely and clear data
          setTimeout(async () => {
            setAnimationState('hidden');
            setCallData(null);

            // Fetch updated thread messages after call ends
            if (threadId && fetchThreadMessagesRef.current) {
              try {
                await fetchThreadMessagesRef.current(threadId);
                console.log(
                  'CallDisplay: Refreshed thread messages after call ended',
                );
              } catch (error) {
                console.error(
                  'CallDisplay: Error fetching thread messages:',
                  error,
                );
              }
            }

            onScrollNeeded?.();
          }, 600);
        }
      },
    );

    // Cleanup on unmount or threadId change
    return () => {
      console.log('CallDisplay: Cleaning up callbacks with ID:', callbackId);
      unregisterPhoneCallStartedCallback(callbackId);
      unregisterPhoneCallEndedCallback(callbackId);
    };
  }, [
    threadId,
    registerPhoneCallStartedCallback,
    unregisterPhoneCallStartedCallback,
    registerPhoneCallEndedCallback,
    unregisterPhoneCallEndedCallback,
  ]);

  // Don't render anything if hidden
  if (animationState === 'hidden') {
    return null;
  }

  return (
    <div className={`call-display call-display--${animationState}`}>
      <div className='phone-icon-holder'>
        <AnimatedPhoneIcon />
      </div>
      {(animationState === 'expanding' || animationState === 'full') && (
        <span>Call in Progress</span>
      )}
    </div>
  );
};

export default CallDisplay;
